import {
  format,
  formatDistanceToNow,
  getTime,
  isBefore,
  isValid,
  parse,
  startOfDay,
} from "date-fns";
import { enGB, es } from "date-fns/locale";

// ----------------------------------------------------------------------

export function fDate(date: Date | string | number, dateFormat?: string) {
  if (!date) return "";
  return format(new Date(date), dateFormat ?? "dd MMMM yyyy");
}

export function fDateTime(
  date: Date | string | number,
  formatStr: string = "dd MMM yyyy p",
) {
  return format(new Date(date), formatStr);
}

export function fTimestamp(date: Date | string | number) {
  return getTime(new Date(date));
}

export function fDateTimeSuffix(date: Date | string | number) {
  return format(new Date(date), "dd/MM/yyyy p");
}

export function fToNow(date: Date | string | number, locale: string) {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: locale === "en" ? enGB : es,
  });
}

export const isDateExpired = (date?: string | number | Date | null) => {
  if (!date) return false;
  const parsed = new Date(date);
  if (!isValid(parsed)) return false;
  return isBefore(startOfDay(parsed), startOfDay(new Date()));
};

export const isDateValid = (dateStr: string) => {
  const parsed = parse(dateStr, "yyyy-MM-dd", new Date());
  if (isValid(parsed)) {
    return true;
  }

  return false;
};

export const parseDate = (dateStr: string | null) => {
  if (!dateStr) {
    return null;
  }
  const parsed = parse(dateStr, "yyyy-MM-dd", new Date());
  if (isValid(parsed)) {
    return parsed;
  }

  return null;
};

export const parseAndFormatDate = (dueDateStr: string | null) => {
  if (!dueDateStr) {
    return null;
  }
  const parsedDate = parseDate(dueDateStr);
  return parsedDate ? format(parsedDate, "dd MMM yyyy") : "invalid_date";
};

// Accepted date-only inputs, tried in order. "dd/MM/yyyy" is day-first to match the
// portal's en-GB / es locales; "dd MMM yyyy" is e.g. "18 Nov 2026".
const PARSEABLE_DATE_FORMATS_V2 = [
  "yyyy-MM-dd",
  "dd/MM/yyyy",
  "dd MMM yyyy",
  "MMMM d, yyyy",
  "MMM d, yyyy",
];

export const parseDateV2 = (dateStr: string | null) => {
  if (!dateStr) {
    return null;
  }
  const trimmed = dateStr.trim();
  for (const dateFormat of PARSEABLE_DATE_FORMATS_V2) {
    const parsed = parse(trimmed, dateFormat, new Date());
    if (isValid(parsed)) {
      return parsed;
    }
  }

  return null;
};

export const isDateValidV2 = (dateStr: string) => parseDateV2(dateStr) !== null;

export const parseAndFormatDateV2 = (dueDateStr: string | null) => {
  if (!dueDateStr) {
    return null;
  }
  const parsedDate = parseDateV2(dueDateStr);
  return parsedDate ? format(parsedDate, "dd MMM yyyy") : "invalid_date";
};

export const spanishDate = () => {
  const date = new Date();
  return format(date, "dd MMM yyyy");
};

export const getCalendarMonthSpan = (
  startDate: Date,
  endDate: Date,
): number => {
  const from = new Date(startOfDay(startDate));
  const to = new Date(startOfDay(endDate));

  const months =
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth()) +
    1;

  return Math.max(months, 1);
};
